import React, { useEffect, useMemo, useRef, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";

import LoadingBlock from "../LoadingBlock/LoadingBlock";
import { baseChartOptions } from "./charts.config";

import exportingModule from "highcharts/modules/exporting";
import offlineExportingModule from "highcharts/modules/offline-exporting";
import exportDataModule from "highcharts/modules/export-data";

function initModule(mod, H) {
  const fn = mod?.default || mod;
  if (typeof fn === "function") fn(H);
}

initModule(exportingModule, Highcharts);
initModule(offlineExportingModule, Highcharts);
initModule(exportDataModule, Highcharts);

export default function TopSubjectsQualitativeChart({
  categories = [],
  positive = [],
  neutral = [],
  negative = [],
  total = [],
  sums = { pos: 0, neu: 0, neg: 0, total: 0 },
  loading = false,
  title = "Top 10 assuntos por análise qualitativa",
  filename = "top-assuntos-qualitativo",
  exportMode = false,
}) {
  const chartRef = useRef(null);

  const CARD_MIN_H = 360;
  const CHART_H = 320;

  const [chartKey, setChartKey] = useState(0);
  useEffect(() => {
    const onEnd = () => setChartKey((k) => k + 1);
    window.addEventListener("dashboard-export:end", onEnd);
    return () => window.removeEventListener("dashboard-export:end", onEnd);
  }, []);

  const wrapStyle = useMemo(() => {
    if (!exportMode) {
      return {
        position: "relative",
        borderRadius: 12,
        padding: 12,
        border: "none",
        background: "var(--background-graphic)",
        boxShadow: "0 8px 20px rgba(15, 23, 42, 0.05)",
        overflow: "hidden",
        minHeight: CARD_MIN_H,
      };
    }

    return {
      position: "relative",
      borderRadius: 0,
      padding: 0,
      border: "none",
      background: "var(--background-graphic)",
      boxShadow: "none",
      overflow: "hidden",
      minHeight: 560,
      width: "100%",
    };
  }, [exportMode]);

  const options = useMemo(() => {
    return {
      ...baseChartOptions,

      chart: {
        type: "column",
        height: exportMode ? 560 : CHART_H,
        spacing: exportMode ? [0, 0, 0, 0] : [24, 16, 16, 24],
        animation: !exportMode,
        backgroundColor: "transparent",
        marginTop: exportMode ? 0 : 72,
      },

      title: {
        text: exportMode ? null : title,
        align: "left",
        x: 0,
        y: -3,
        margin: 12,
        style: { fontSize: "14px", fontWeight: "800", color: "#614a9e" },
      },

      exporting: { enabled: false },

      xAxis: {
        categories,
        lineWidth: 0,
        tickLength: 0,
        labels: {
          style: {
            fontSize: exportMode ? "12px" : "11px",
            fontWeight: "700",
            color: "var(--text-color)",
          },
        },
      },

      yAxis: {
        min: 0,
        title: { text: null },
        gridLineColor: "rgba(17,24,39,0.10)",
        gridLineDashStyle: "ShortDot",
        labels: {
          style: {
            fontSize: exportMode ? "12px" : "11px",
            fontWeight: "700",
            color: "var(--text-color)",
          },
        },
        stackLabels: {
          enabled: true,
          style: {
            fontWeight: "900",
            fontSize: exportMode ? "12px" : "11px",
            color: "var(--text-color)",
            textOutline: "none",
          },
          formatter: function () {
            return this.total > 0 ? this.total : "";
          },
        },
      },

      legend: exportMode
        ? { enabled: false }
        : {
            align: "center",
            verticalAlign: "top",
            layout: "horizontal",
            symbolRadius: 99,
            itemStyle: {
              fontWeight: "700",
              fontSize: "12px",
              color: "var(--text-color)",
            },
            labelFormatter: function () {
              const name = this.name;
              if (name === "Positiva") return `Positiva (${sums.pos})`;
              if (name === "Neutra") return `Neutra (${sums.neu})`;
              if (name === "Negativa") return `Negativa (${sums.neg})`;
              if (name === "Total") return `Total (${sums.total})`;
              return name;
            },
          },

      tooltip: {
        shared: true,
        useHTML: true,
        formatter: function () {
          const idx = this.points?.[0]?.point?.index ?? 0;

          const t = total[idx] ?? 0;
          const p = positive[idx] ?? 0;
          const n = neutral[idx] ?? 0;
          const ng = negative[idx] ?? 0;

          return `
            <div style="font-weight:900;margin-bottom:6px">${categories[idx]}</div>
            <div>Total: <b>${t}</b></div>
            <div>Positiva: <b>${p}</b></div>
            <div>Neutra: <b>${n}</b></div>
            <div>Negativa: <b>${ng}</b></div>
          `;
        },
      },

      plotOptions: {
        column: {
          stacking: "normal",
          borderWidth: 0,
          dataLabels: {
            enabled: true,
            style: {
              fontWeight: "900",
              fontSize: exportMode ? "17px" : "11px",
              color: "#fff",
              textOutline: "none",
            },
            formatter: function () {
              return this.y > 0 ? this.y : "";
            },
          },
        },
      },

      series: [
        { name: "Positiva", data: positive, color: "#16A34A" },
        { name: "Neutra", data: neutral, color: "#F59E0B" },
        { name: "Negativa", data: negative, color: "#EF4444" },
        {
          name: "Total",
          type: "line",
          data: total,
          color: "var(--text-color)",
          visible: false,
          enableMouseTracking: false,
        },
      ],

      credits: { enabled: false },
    };
  }, [categories, positive, neutral, negative, total, sums, title, filename, exportMode]);

  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (!chart) return;

    const t = setTimeout(() => {
      try {
        chart.reflow();

        if (exportMode) {
          const w = chart.renderTo?.clientWidth || null;
          chart.setSize(w, 560, false);
          chart.reflow();
        }
      } catch {}
    }, exportMode ? 60 : 0);

    return () => clearTimeout(t);
  }, [exportMode, categories, positive, neutral, negative, total]);

  const handleExportPNG = () => {
    const chart = chartRef.current?.chart;
    if (!chart) return;

    chart.exportChart(
      { type: "image/png", filename },
      { chart: { backgroundColor: "var(--background-graphic)" } }
    );
  };

  const exportBtnStyle = {
    position: "absolute",
    top: 10,
    right: 10,
    zIndex: 5,

    height: 34,
    padding: "0 12px",
    borderRadius: 12,
    border: "none",
    background: "var(--bottom-dropdown-color)",
    color: "var(--text-color)",
    fontWeight: 900,
    fontSize: 12,

    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    cursor: "pointer",
    userSelect: "none",
    transition: "transform .08s ease",
  };

  const exportBtnHoverStyle = {
    boxShadow: "0 6px 14px rgba(97, 74, 158, 0.15)",
    backgroundColor: "#614a9e",
    color: "white",
  };

  const iconStyle = {
    width: 18,
    height: 18,
    display: "grid",
    placeItems: "center",
    borderRadius: 8,
    background:
      "linear-gradient(135deg, var(--icon-gradient-start), var(--icon-gradient-end))",
    color: "var(--color-text-button-action)",
    fontSize: 12,
    lineHeight: 1,
    flex: "0 0 auto",
  };

  return (
    <LoadingBlock loading={loading} label="Carregando top assuntos (qualitativo)...">
      <div style={wrapStyle}>
        {!exportMode ? (
          <ExportButton
            onClick={handleExportPNG}
            style={exportBtnStyle}
            hoverStyle={exportBtnHoverStyle}
            iconStyle={iconStyle}
          />
        ) : null}

        <HighchartsReact
          key={chartKey}
          ref={chartRef}
          highcharts={Highcharts}
          options={options}
          containerProps={{ style: { height: exportMode ? 560 : CHART_H } }}
        />
      </div>
    </LoadingBlock>
  );
}

function ExportButton({ onClick, style, hoverStyle, iconStyle }) {
  const [hover, setHover] = useState(false);

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ ...style, ...(hover ? hoverStyle : {}) }}
      title="Exportar PNG"
    >
      <span style={iconStyle}>⭳</span>
      Exportar PNG
    </button>
  );
}
